import client from "@/client/publicApiClient.ts";
import { useQuery, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import type { GetItchConnectionQueryResponse, GetItchConnectionPathParams, GetItchConnection404 } from "../types/GetItchConnection";
import type { QueryObserverOptions, UseQueryResult, QueryKey, UseSuspenseQueryOptions, UseSuspenseQueryResult } from "@tanstack/react-query";

 type GetItchConnectionClient = typeof client<GetItchConnectionQueryResponse, GetItchConnection404, never>;
type GetItchConnection = {
    data: GetItchConnectionQueryResponse;
    error: GetItchConnection404;
    request: never;
    pathParams: GetItchConnectionPathParams;
    queryParams: never;
    headerParams: never;
    response: GetItchConnectionQueryResponse;
    client: {
        parameters: Partial<Parameters<GetItchConnectionClient>[0]>;
        return: Awaited<ReturnType<GetItchConnectionClient>>;
    };
};
export const getItchConnectionQueryKey = (clientCode: GetItchConnectionPathParams["clientCode"], itchConnectionId: GetItchConnectionPathParams["itchConnectionId"]) => [{ url: "/api/clients/:clientCode/itch-connections/:itchConnectionId", params: { clientCode: clientCode, itchConnectionId: itchConnectionId } }] as const;
export type GetItchConnectionQueryKey = ReturnType<typeof getItchConnectionQueryKey>;
export function getItchConnectionQueryOptions(clientCode: GetItchConnectionPathParams["clientCode"], itchConnectionId: GetItchConnectionPathParams["itchConnectionId"], options: GetItchConnection["client"]["parameters"] = {}) {
    const queryKey = getItchConnectionQueryKey(clientCode, itchConnectionId);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetItchConnection["data"], GetItchConnection["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/itch-connections/${itchConnectionId}`,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets a single fix session for a given client and fix session id.
 * @summary Get itch connection
 * @link /api/clients/:clientCode/itch-connections/:itchConnectionId
 */
export function useGetItchConnection<TData = GetItchConnection["response"], TQueryData = GetItchConnection["response"], TQueryKey extends QueryKey = GetItchConnectionQueryKey>(clientCode: GetItchConnectionPathParams["clientCode"], itchConnectionId: GetItchConnectionPathParams["itchConnectionId"], options: {
    query?: Partial<QueryObserverOptions<GetItchConnection["response"], GetItchConnection["error"], TData, TQueryData, TQueryKey>>;
    client?: GetItchConnection["client"]["parameters"];
} = {}): UseQueryResult<TData, GetItchConnection["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getItchConnectionQueryKey(clientCode, itchConnectionId);
    const query = useQuery({
        ...getItchConnectionQueryOptions(clientCode, itchConnectionId, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseQueryResult<TData, GetItchConnection["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}
export const getItchConnectionSuspenseQueryKey = (clientCode: GetItchConnectionPathParams["clientCode"], itchConnectionId: GetItchConnectionPathParams["itchConnectionId"]) => [{ url: "/api/clients/:clientCode/itch-connections/:itchConnectionId", params: { clientCode: clientCode, itchConnectionId: itchConnectionId } }] as const;
export type GetItchConnectionSuspenseQueryKey = ReturnType<typeof getItchConnectionSuspenseQueryKey>;
export function getItchConnectionSuspenseQueryOptions(clientCode: GetItchConnectionPathParams["clientCode"], itchConnectionId: GetItchConnectionPathParams["itchConnectionId"], options: GetItchConnection["client"]["parameters"] = {}) {
    const queryKey = getItchConnectionSuspenseQueryKey(clientCode, itchConnectionId);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetItchConnection["data"], GetItchConnection["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/itch-connections/${itchConnectionId}`,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets a single fix session for a given client and fix session id.
 * @summary Get itch connection
 * @link /api/clients/:clientCode/itch-connections/:itchConnectionId
 */
export function useGetItchConnectionSuspense<TData = GetItchConnection["response"], TQueryKey extends QueryKey = GetItchConnectionSuspenseQueryKey>(clientCode: GetItchConnectionPathParams["clientCode"], itchConnectionId: GetItchConnectionPathParams["itchConnectionId"], options: {
    query?: Partial<UseSuspenseQueryOptions<GetItchConnection["response"], GetItchConnection["error"], TData, TQueryKey>>;
    client?: GetItchConnection["client"]["parameters"];
} = {}): UseSuspenseQueryResult<TData, GetItchConnection["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getItchConnectionSuspenseQueryKey(clientCode, itchConnectionId);
    const query = useSuspenseQuery({
        ...getItchConnectionSuspenseQueryOptions(clientCode, itchConnectionId, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseSuspenseQueryResult<TData, GetItchConnection["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}